import React, { useEffect, useState } from "react";
import Modal from "@mui/material/Modal";
import "./ImagePreviewModal.scss";
import CloseIcon from "../../../../components/icons/CloseIcon";
import LeftArrowIcon from "../../../../components/icons/LeftArrowIcon";
import RightArrowIcon from "../../../../components/icons/RightArrowIcon";

interface ImagePreviewModalProps {
  open: boolean;
  onClose: () => void;
  images: {
    medium: string;
    large: string;
  }[];
  startIndex?: number;
}

const ImagePreviewModal: React.FC<ImagePreviewModalProps> = ({
  open,
  onClose,
  images,
  startIndex = 0,
}) => {
  const [current, setCurrent] = useState(startIndex);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 480);
  const [isImageLoading, setIsImageLoading] = useState(true);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 480);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (startIndex !== undefined) {
      setCurrent(startIndex);
    }
  }, [startIndex, open]);

  const handlePrev = () => {
    if (current > 0) {
      setCurrent((prev) => prev - 1);
    }
  };

  const handleNext = () => {
    if (current < images.length - 1) {
      setCurrent((prev) => prev + 1);
    }
  };

  const handleImageClick = (e: React.MouseEvent<HTMLImageElement>) => {
    const img = e.currentTarget;

    const rect = img.getBoundingClientRect();

    const naturalW = img.naturalWidth;
    const naturalH = img.naturalHeight;

    if (!naturalW || !naturalH) {
      e.stopPropagation();
      return;
    }

    const containerW = rect.width;
    const containerH = rect.height;

    const imageAspect = naturalW / naturalH;
    const containerAspect = containerW / containerH;

    let renderedW: number;
    let renderedH: number;

    if (imageAspect > containerAspect) {
      renderedW = containerW;
      renderedH = containerW / imageAspect;
    } else {
      renderedH = containerH;
      renderedW = containerH * imageAspect;
    }

    const centerX = rect.left + containerW / 2;
    const centerY = rect.top + containerH / 2;

    const imgLeft = centerX - renderedW / 2;
    const imgRight = centerX + renderedW / 2;
    const imgTop = centerY - renderedH / 2;
    const imgBottom = centerY + renderedH / 2;

    const clickX = e.clientX;
    const clickY = e.clientY;

    const insideImage =
      clickX >= imgLeft &&
      clickX <= imgRight &&
      clickY >= imgTop &&
      clickY <= imgBottom;

    if (insideImage) {
      e.stopPropagation();
    } else {
      e.stopPropagation();
      onClose();
    }
  };

  useEffect(() => {
    setIsImageLoading(true);
  }, [current, open]);

  const currentImage = images[current];
  const currentSrc = currentImage?.large || currentImage?.medium;

  if (!images || images.length === 0) return null;

  return (
    <Modal open={open} onClose={onClose}>
      <div className="preview-modal-backdrop" onClick={onClose}>
        <div className="preview-modal">
          <button className="close-btn" onClick={onClose}>
            <CloseIcon />
          </button>

          <div className="nav-controls">
            <button
              type="button"
              className={`nav left ${current === 0 ? "disabled" : ""}`}
              onClick={(e) => {
                e.stopPropagation();
                if (current === 0) return;
                handlePrev();
              }}
              disabled={current === 0}
              aria-label="Previous image"
            >
              <LeftArrowIcon />
            </button>

            <button
              type="button"
              className={`nav right ${
                current === images.length - 1 ? "disabled" : ""
              }`}
              onClick={(e) => {
                e.stopPropagation();
                if (current === images.length - 1) return;
                handleNext();
              }}
              disabled={current === images.length - 1}
              aria-label="Next image"
            >
              <RightArrowIcon />
            </button>
          </div>

          <div className="image-wrapper">
            {isImageLoading && (
              <div className="modal-skeleton">
                <div className="skeleton-box"></div>
              </div>
            )}
            <img
              src={currentSrc}
              alt={`slide-${current}`}
              onLoad={() => setIsImageLoading(false)}
              onClick={handleImageClick}
              style={{
                visibility: isImageLoading ? "hidden" : "visible",
                width: "100%",
                height: "100%",
                objectFit: "contain",
              }}
            />
          </div>

          <div className="counter">
            {current + 1}/{images.length}
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ImagePreviewModal;
